import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import { SelectorOptionsMulti, SpacerVertical, SpacerHorizontal } from '@klarna/bubble-ui';
import { translator } from 'mage-i18n';
import { defaultContainerPropTypes, HorizontalWrapper, payloadUtils } from 'moet-core';
import { generateCyElemId, generateCySelectable } from 'moet-test-utils/cypress/utils';

import {
  CY_BUSINESS_CONCEPT_CONTAINER_IDS,
  CY_BUSINESS_CONCEPT_OPTION_IDS,
  tPrefix,
} from './constants';
import { CAPS, THRESHOLD_BUSINESS_CONCEPTS } from '@/constants';
import { CallToActionButton, ContainerTemplate, BackButton } from '@/components';
import { withCapabilityBeforeNext } from '@/flow/common';
import { getDefaultState } from './selectors';
import {
  getBusinessConceptsFilteredByCountry,
  hasHighExposure,
  shouldRenderPMLQuestionnaires,
} from '@/selectors/merchant';

export class BusinessConcept extends React.Component {
  constructor(props) {
    super(props);

    this.initOptions = this.initOptions.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.getBody = this.getBody.bind(this);
    this.onNext = this.onNext.bind(this);

    const {
      defaultState: { businessConcepts: selectedKeys = [] },
    } = props;
    const options = this.initOptions(selectedKeys);

    this.state = { options, selectedKeys, initialState: { options, selectedKeys }, tError: null };
  }

  initOptions(selectedKeys = []) {
    const { businessConcepts: options } = this.props;
    return options.map(option => ({
      ...option,
      key: option.value,
      ...generateCyElemId(CY_BUSINESS_CONCEPT_OPTION_IDS[option.value], window),
      ...generateCySelectable(selectedKeys.includes(option.value), window),
    }));
  }

  onSelect(selectedKeys) {
    this.setState({ selectedKeys, tError: null });
  }

  getBody(state) {
    const { isHighPML } = this.props;
    const businessConcepts = state.options
      .map(({ key }) => key)
      .filter(key => state.selectedKeys.includes(key));

    const shouldShowPML =
      isHighPML &&
      businessConcepts.every(
        businessConcept => !THRESHOLD_BUSINESS_CONCEPTS.includes(businessConcept),
      );
    // We need to check against the current business concepts

    return {
      businessConcepts,
      shouldShowPML,
    };
  }

  onNext() {
    const atLeastOneSelected = this.state.selectedKeys.length > 0;

    if (!atLeastOneSelected) {
      return this.setState({ tError: `${tPrefix}.errors.select_at_least_one` });
    }

    const payload = payloadUtils.getMerchantCapabilityPayload(
      this.props.mid,
      {
        ...this.state,
        initialState: null, // Always trigger capability call
      },
      this.getBody,
      'BusinessConceptContainer',
    );

    this.props.onNext(payload);
  }

  render() {
    const { error, hasBack, isDisabled, isLoading, onBack, t } = this.props;
    const { selectedKeys, tError: tErrorFromState } = this.state;
    return (
      <ContainerTemplate
        title={t(`${tPrefix}.title`)}
        subtitle={t(`${tPrefix}.subtitle`)}
        error={error}
        tFrontEndError={tErrorFromState}
        scrollIntoView={Boolean(error || tErrorFromState)}
      >
        <SpacerVertical spaceToken="space/150" />
        <SelectorOptionsMulti
          name="options-regular"
          options={this.state.options}
          values={selectedKeys}
          onSelect={this.onSelect}
        />
        <SpacerVertical spaceToken="space/600" />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && (
            <>
              <BackButton
                onClick={onBack}
                disabled={isDisabled}
                {...generateCyElemId(CY_BUSINESS_CONCEPT_CONTAINER_IDS.BTN_BACK, window)}
              >
                {t(`${tPrefix}.buttons.back`)}
              </BackButton>
              <SpacerHorizontal spaceToken="space/300" />
            </>
          )}
          <CallToActionButton
            loading={isLoading}
            onClick={this.onNext}
            disabled={isDisabled}
            {...generateCyElemId(CY_BUSINESS_CONCEPT_CONTAINER_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.next`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

BusinessConcept.propTypes = {
  ...defaultContainerPropTypes,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  defaultState: PropTypes.object,
  isLoading: PropTypes.bool,
};

export default compose(
  connect(state => ({
    businessConcepts: getBusinessConceptsFilteredByCountry(state),
    defaultState: getDefaultState(state),
    isHighExposure: hasHighExposure(state),
    isHighPML: shouldRenderPMLQuestionnaires(state),
    t: translator(state),
  })),
  withCapabilityBeforeNext({
    cap: CAPS.V2_PRE_ASSESSMENT_PATCH,
  }),
)(BusinessConcept);
